import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { SmsReminders, formatConsentDay } from './SmsReminders';
import type { ClientDetail } from '../wire';
import { useLocale, useT } from '../messages';
import type { T, UiLocale } from '../messages';

/**
 * **What this business has her permission to do** — one group, three ticks.
 *
 * ## Why this file exists
 *
 * Three waves landed on one tree on 2026-09-20 and each put a permission on the
 * client record without seeing the others. What an owner met the next morning
 * was: an SMS control with a heading, a script and a since-when line; a
 * newsletter checkbox with no heading, no explanation where there was no
 * address, and no provenance at all; and a *"let her book herself"* switch that
 * existed only as a one-tap button on a pending request — so a regular with
 * nothing pending could not be trusted, although `core-api` had been serving
 * `may_book_online` on this very projection all along with a comment saying it
 * was *"read by the client record's own control"*.
 *
 * Three ticks written by three hands read as three different kinds of thing.
 * They are one kind of thing: **standing permissions a person gave, which the
 * business can revoke and has to be able to account for.** So they are one
 * group, with one shape per row — *tick · why · since when* — and one sentence
 * where a permission cannot apply.
 *
 * ## What is deliberately *not* unified
 *
 * **The three writes stay three writes.** Each tick posts its own one-field
 * `client.update` through the outbox, exactly as before. A combined "keep in
 * touch" switch would be the convenience that becomes a complaint: agreeing to
 * a reminder about tomorrow is not agreeing to marketing (TCPA and CAN-SPAM are
 * not the same law), and neither is agreeing to be allowed to book. Grouping
 * them on a screen is a claim about what they *are*; merging them would be a
 * claim about what she *said*.
 *
 * **The appointment sheet still draws the SMS control alone.** That sheet is
 * open while she is in the room, which is the one moment *"is it OK if we text
 * you a reminder?"* is a question somebody can actually ask; "may she book
 * herself online" is not a question for a treatment room.
 *
 * ## Since-when, and the one row that has none
 *
 * Two of the three carry provenance, because migrations 0041 and 0042 record
 * *when* and *how* the flag last moved **in either direction** — the half of a
 * consent record with a regulator attached. `may_book_online` has neither
 * column: 0040 gave it a `NOT NULL DEFAULT false` and no pair, and this group
 * says so in words rather than leaving a gap that reads as "recently". Adding
 * that pair is a migration and is named in `docs/lovespa-gaps-plan.md`, not
 * guessed at here.
 */
export function Permissions({ client }: { client: ClientDetail }): ReactNode {
  const t = useT();

  return (
    <section className="permissions" aria-labelledby="permissions-title">
      <h2 id="permissions-title">{t('permissions.heading')}</h2>
      <p className="muted">{t('permissions.note')}</p>

      {/* The SMS control is its own component because it is drawn in two
          places — here and on the appointment sheet. It renders its own row in
          this group's shape and its heading is suppressed, because the group
          owns the heading now. */}
      <SmsReminders client={client} heading={false} />

      <NewsletterTick client={client} />
      <MayBookOnline client={client} />
    </section>
  );
}

/**
 * **"Email her our newsletter"** — E3's one control, and the only thing in this
 * app that can put a person on a mailing list.
 *
 * Moved here out of `Clients.tsx` unchanged in behaviour and completed in two
 * ways it was short: it now says why it is absent where there is no address
 * (the sentence its own comment claimed it printed and did not), and it prints
 * since-when-and-how, which migration 0042 has been recording since the day the
 * column existed and no projection carried until the integration pass.
 */
function NewsletterTick({ client }: { client: ClientDetail }): ReactNode {
  const actions = useActions();
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';

  const hasEmail = client.identities.some((i) => i.identity_kind === 'email');

  return (
    <div className="permission">
      {hasEmail ? (
        <>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={client.email_marketing_opt_in}
              onChange={(e) => {
                void actions.updateClient({
                  clientId: client.client_id,
                  name: client.display_name,
                  // One field, for `SmsReminders`' reason: a consent write that
                  // carried unrelated edits makes "who changed this, and when" a
                  // question about a diff rather than about an intent.
                  changes: { email_marketing_opt_in: e.target.checked },
                });
              }}
            />
            <span>{t('permissions.newsletterTick')}</span>
          </label>
          <p className="muted">
            {client.email_marketing_opt_in
              ? emailProvenance(client, timezone, runtime.now(), locale, t)
              : t('permissions.newsletterOff')}
          </p>
        </>
      ) : (
        <p className="muted">{t('permissions.noEmail')}</p>
      )}
    </div>
  );
}

/**
 * **"She may book herself online"** — A5's per-client half of
 * `guest_booking: 'trusted'`.
 *
 * It is drawn for every client rather than only under `trusted`, deliberately
 * and unlike the console's `request_hold_hours` control: an owner grants trust
 * to a person *when she decides to*, which is often the visit before she
 * switches her site over, and a control that appeared with a setting would mean
 * flipping the site first and then going back through the book. The hint says
 * what the flag does and does not do, so a tick on an `open` tenant is a
 * recorded decision rather than a confusing no-op.
 *
 * **No provenance line**, and that is a statement rather than an omission —
 * migration 0040 gave this column no `_at`/`_source` pair, so the honest answer
 * to "since when" is that nobody recorded it. Saying so is the same discipline
 * `sms.sinceUnknown` applies to a pre-0041 row.
 */
function MayBookOnline({ client }: { client: ClientDetail }): ReactNode {
  const actions = useActions();
  const t = useT();

  return (
    <div className="permission">
      <label className="checkbox">
        <input
          type="checkbox"
          checked={client.may_book_online}
          onChange={(e) => {
            void actions.updateClient({
              clientId: client.client_id,
              name: client.display_name,
              changes: { may_book_online: e.target.checked },
            });
          }}
        />
        <span>{t('permissions.bookOnlineTick')}</span>
      </label>
      <p className="muted">{t('permissions.bookOnlineNote')}</p>
    </div>
  );
}

/**
 * "Since 3 March — you ticked it", one channel over from `provenanceLine`.
 *
 * A second small table rather than a shared one, because the two sources lists
 * are deliberately **different**: there is no `reply_yes` for email (it has no
 * keyword channel) and there is a `bounce`, because a dead mailbox takes the
 * flag off by itself and an owner reading the record should be able to tell
 * "she left" from "her mailbox is gone". A shared mapping would have to hold
 * both and would quietly render an impossible one.
 */
function emailProvenance(
  client: ClientDetail,
  timezone: string,
  nowMs: number,
  locale: UiLocale,
  t: T,
): string {
  const at = client.email_marketing_opt_in_at ?? null;
  const source = client.email_marketing_opt_in_source ?? null;
  if (at === null) return t('permissions.newsletterSinceUnknown');
  return t('permissions.newsletterSince', {
    day: formatConsentDay(at, timezone, nowMs, locale),
    how: t(EMAIL_HOW_KEYS[source ?? 'import_none']),
  });
}

const EMAIL_HOW_KEYS = {
  staff: 'permissions.emailHowStaff',
  guest_site: 'permissions.emailHowGuestSite',
  unsubscribe: 'permissions.emailHowUnsubscribe',
  bounce: 'permissions.emailHowBounce',
  import_none: 'permissions.emailHowImport',
  // The guest asked to be forgotten and the owner ran it (spec §7.6, 0061).
  anonymized: 'permissions.emailHowAnonymized',
} as const satisfies Record<NonNullable<ClientDetail['email_marketing_opt_in_source']>, string>;
